import { toolText, type Locale, type ToolId } from './i18n'
import { pathFor, relatedToolIds, type AppRoute } from './routes'

export type SeoPayload = {
  title: string
  description: string
  canonical: string
  lang: string
  ogLocale: string
  alternates: Array<{ hreflang: string; href: string }>
  jsonLd: unknown[]
}

const homeCopy: Record<Locale, { title: string; description: string }> = {
  en: {
    title: 'Private Local Video & Audio Tools | Frame Local',
    description: 'Compress, convert, trim and edit video or audio locally in your browser. Twenty private tools with no media uploads.',
  },
  zh: {
    title: '本地视频与音频工具｜文件不上传｜帧内',
    description: '在浏览器本地压缩、转换、裁剪和编辑视频音频。20 个隐私优先工具，媒体文件不会上传。',
  },
  ja: {
    title: 'ローカル動画・音声ツール｜アップロード不要｜Frame Local',
    description: 'ブラウザ内で動画と音声を圧縮、変換、トリミング。20種類のツールをファイル送信なしで利用できます。',
  },
}

const langTag: Record<Locale, string> = { en: 'en', zh: 'zh-CN', ja: 'ja' }
const ogLocale: Record<Locale, string> = { en: 'en_US', zh: 'zh_CN', ja: 'ja_JP' }

function absolute(siteUrl: string, path: string): string {
  return new URL(path, `${siteUrl}/`).href.replace(/\/$/, path === '/' ? '/' : '')
}

export function normalizeSiteUrl(value: string): string {
  let parsed: URL
  try {
    parsed = new URL(value)
  } catch {
    throw new Error('PUBLIC_SITE_URL must be an absolute HTTPS URL, for example https://media.example.com')
  }
  if (parsed.protocol !== 'https:' || parsed.pathname !== '/' || parsed.search || parsed.hash) {
    throw new Error('PUBLIC_SITE_URL must contain only an HTTPS origin, without a path, query or hash')
  }
  return parsed.origin
}

export function seoFor(route: AppRoute, siteUrl: string): SeoPayload {
  const normalizedSiteUrl = normalizeSiteUrl(siteUrl)
  const tool = route.toolId ? toolText(route.locale, route.toolId) : null
  const title = tool
    ? route.locale === 'en'
      ? `${tool.name} Online — Private Local Tool | Frame Local`
      : route.locale === 'zh'
        ? `${tool.name}在线工具｜浏览器本地处理｜帧内`
        : `${tool.name}オンライン｜ブラウザ内ローカル処理｜Frame Local`
    : homeCopy[route.locale].title
  const description = tool
    ? route.locale === 'en'
      ? `${tool.description} Process files privately on this device with no media upload.`
      : route.locale === 'zh'
        ? `${tool.description}文件只在此设备处理，不上传媒体内容。`
        : `${tool.description}ファイルはこの端末内だけで処理され、アップロードされません。`
    : homeCopy[route.locale].description
  const canonical = absolute(normalizedSiteUrl, pathFor(route.locale, route.toolId))
  const alternates = [
    { hreflang: 'en', href: absolute(normalizedSiteUrl, pathFor('en', route.toolId)) },
    { hreflang: 'zh-CN', href: absolute(normalizedSiteUrl, pathFor('zh', route.toolId)) },
    { hreflang: 'ja', href: absolute(normalizedSiteUrl, pathFor('ja', route.toolId)) },
    { hreflang: 'x-default', href: absolute(normalizedSiteUrl, pathFor('en', route.toolId)) },
  ]

  const jsonLd = route.toolId
    ? [
        {
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: tool?.name,
          description,
          url: canonical,
          applicationCategory: route.toolId.startsWith('video-') ? 'MultimediaApplication' : 'AudioApplication',
          operatingSystem: 'Any modern browser',
          browserRequirements: 'Requires JavaScript, WebAssembly and Web Workers',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          featureList: ['Local browser processing', 'No media upload', tool?.outputFormats],
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: route.locale === 'en' ? 'Home' : route.locale === 'zh' ? '首页' : 'ホーム',
              item: absolute(normalizedSiteUrl, pathFor(route.locale)),
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: tool?.name,
              item: canonical,
            },
          ],
        },
      ]
    : [
        {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Frame Local',
          alternateName: route.locale === 'zh' ? '帧内' : 'Local Media Kit',
          url: canonical,
          inLanguage: langTag[route.locale],
        },
      ]

  return {
    title,
    description,
    canonical,
    lang: langTag[route.locale],
    ogLocale: ogLocale[route.locale],
    alternates,
    jsonLd,
  }
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
}

export function renderSeoHead(payload: SeoPayload): string {
  const alternates = payload.alternates
    .map(({ hreflang, href }) => `<link rel="alternate" hreflang="${hreflang}" href="${escapeHtml(href)}">`)
    .join('\n    ')
  const jsonLd = payload.jsonLd
    .map((value) => `<script type="application/ld+json">${JSON.stringify(value).replaceAll('<', '\\u003c')}</script>`)
    .join('\n    ')
  return `<title>${escapeHtml(payload.title)}</title>
    <meta name="description" content="${escapeHtml(payload.description)}">
    <meta name="robots" content="index,follow">
    <link rel="canonical" href="${escapeHtml(payload.canonical)}">
    ${alternates}
    <meta property="og:type" content="website">
    <meta property="og:title" content="${escapeHtml(payload.title)}">
    <meta property="og:description" content="${escapeHtml(payload.description)}">
    <meta property="og:url" content="${escapeHtml(payload.canonical)}">
    <meta property="og:locale" content="${payload.ogLocale}">
    <meta name="twitter:card" content="summary">
    <meta name="twitter:title" content="${escapeHtml(payload.title)}">
    <meta name="twitter:description" content="${escapeHtml(payload.description)}">
    ${jsonLd}`
}

export function applySeoToDocument(route: AppRoute, siteUrl: string): void {
  const payload = seoFor(route, siteUrl)
  document.documentElement.lang = payload.lang
  document.title = payload.title
  document.querySelectorAll([
    'meta[name="description"]',
    'meta[name="robots"]',
    'meta[property^="og:"]',
    'meta[name^="twitter:"]',
    'link[rel="canonical"]',
    'link[rel="alternate"]',
    'script[type="application/ld+json"]',
  ].join(',')).forEach((node) => node.remove())
  const fragment = document.createRange().createContextualFragment(
    renderSeoHead(payload)
      .replace(/<title>[\s\S]*?<\/title>/, '')
      .replaceAll('<meta ', '<meta data-managed-seo ')
      .replaceAll('<link ', '<link data-managed-seo ')
      .replaceAll('<script ', '<script data-managed-seo '),
  )
  document.head.append(fragment)
}

export function relatedFor(locale: Locale, toolId: ToolId) {
  return relatedToolIds(toolId).map((id) => ({ id, ...toolText(locale, id) }))
}
